import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CalendarHeart, CircleHelp, ClipboardList, Gift, Globe, HandHeart, Handshake, Heart,
  History, Home, Images, Inbox, KeyRound, LayoutDashboard, LifeBuoy, LogOut, Mail,
  Newspaper, Phone, Search, ExternalLink, Eye, RotateCcw, Save, Send, Ban,
} from "lucide-react";
import { api, setToken } from "./api";
import { LoginForm } from "./LoginForm";
import { AREAS } from "./areas";
import { FieldsPart, ListPart } from "./editors";
import * as defaults from "../content/site";
import { Logo } from "../components/Logo";

const DEFAULT_BY_KEY = {
  site: defaults.SITE,
  crisis: defaults.CRISIS,
  home: defaults.HOME_PAGE,
  about: defaults.ABOUT_PAGE,
  event: defaults.EVENT,
  registration: defaults.REGISTRATION,
  faqs: defaults.WALK_FAQS,
  resources: defaults.RESOURCES,
  gallery: defaults.GALLERY,
  news: defaults.ANNOUNCEMENTS,
  contact: defaults.CONTACT,
  volunteer: defaults.VOLUNTEER,
  sponsor: defaults.SPONSOR,
  donations: defaults.DONATIONS,
  seo: defaults.SEO,
  images: defaults.IMAGES,
};

const AREA_ICONS = {
  home: Home, about: Heart, gallery: Images, event: CalendarHeart,
  registration: ClipboardList, faqs: CircleHelp, news: Newspaper,
  resources: LifeBuoy, volunteer: HandHeart, sponsor: Handshake,
  donations: Gift, contact: Mail, site: Globe, crisis: Phone, seo: Search,
  images: Images,
};

const Badge = ({ children, tone = "neutral" }) => {
  const tones = {
    neutral: "bg-paper text-body border-sage/15",
    good: "bg-gold-soft/70 text-ink border-gold/40",
    warn: "bg-plum-soft text-plum-dark border-plum/30",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tones[tone]}`}>
      {children}
    </span>
  );
};

function StatusMsg({ status }) {
  if (!status) return null;
  return (
    <p
      role="status"
      className={`rounded-lg p-3 text-sm font-medium ${
        status.kind === "error" ? "border border-red-200 bg-red-50 text-red-800" : "bg-gold-soft/60 text-ink"
      }`}
      data-testid="admin-status-message"
    >
      {status.msg}
    </p>
  );
}

function ContentEditor({ area }) {
  const [draft, setDraft] = useState(null);
  const [meta, setMeta] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const d = await api(`/admin/content/${area.key}`);
    const base = d.draft ?? d.published ?? DEFAULT_BY_KEY[area.key];
    setDraft(JSON.parse(JSON.stringify(base)));
    setMeta(d);
  };

  useEffect(() => {
    setDraft(null);
    setStatus(null);
    load().catch((e) => setStatus({ kind: "error", msg: e.message }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area.key]);

  if (!draft || !meta) {
    return <p role="status" className="p-8 text-body">Loading {area.label}…</p>;
  }

  const countPlaceholders = (v) =>
    v == null || v === "" ? 1 : Array.isArray(v) ? v.reduce((n, x) => n + countPlaceholders(x), 0)
    : typeof v === "object" ? Object.values(v).reduce((n, x) => n + countPlaceholders(x), 0) : 0;
  const placeholders = countPlaceholders(draft);

  const run = async (fn, okMsg) => {
    setBusy(true);
    setStatus(null);
    try {
      await fn();
      await load();
      setStatus({ kind: "ok", msg: okMsg });
    } catch (e) {
      setStatus({ kind: "error", msg: e.message });
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = () => run(() => api(`/admin/content/${area.key}`, { method: "PUT", body: { draft } }), "Draft saved. Nothing is public yet.");
  const publish = () => {
    if (area.key === "crisis" && JSON.stringify(draft) !== JSON.stringify(DEFAULT_BY_KEY.crisis)) {
      const ok = window.confirm(
        "You are changing the crisis / Get Help Now links. These appear on every page and people may rely on them in an emergency. Continue publishing?"
      );
      if (!ok) return;
    }
    run(() => api(`/admin/content/${area.key}/publish`, { method: "POST" }), "Published. The live site now shows this version.");
  };
  const discard = () => {
    if (window.confirm("Discard your unpublished draft changes?")) {
      run(() => api(`/admin/content/${area.key}/discard-draft`, { method: "POST" }), "Draft discarded.");
    }
  };
  const restore = () => {
    if (window.confirm("Restore the previous published version? The current published version will be replaced.")) {
      run(() => api(`/admin/content/${area.key}/restore`, { method: "POST" }), "Restored the previous published version.");
    }
  };
  const reset = () => {
    if (
      window.confirm(
        `Reset "${area.label}" to the built-in default content? Your published version, draft, and previous versions for this area will be removed.`
      )
    ) {
      run(() => api(`/admin/content/${area.key}/reset`, { method: "POST" }), "Reset to the built-in defaults.");
    }
  };
  const preview = () =>
    window.open(`${area.preview || "/"}${area.preview?.includes("?") ? "&" : "?"}preview=1`, "_blank", "noopener");

  const isList = !!area.listSchema;

  return (
    <div data-testid={`editor-${area.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">{area.label}</h2>
          <p className="mt-1 max-w-2xl text-sm leading-relaxed text-body">{area.help}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {meta.published != null ? <Badge tone="good">Published</Badge> : <Badge>Using built-in defaults</Badge>}
          {meta.draft != null && <Badge tone="warn">Unpublished draft</Badge>}
          {placeholders > 0 && <Badge tone="warn">{placeholders} placeholder field{placeholders === 1 ? "" : "s"}</Badge>}
          {meta.revisions > 0 && <Badge>{meta.revisions} previous version{meta.revisions === 1 ? "" : "s"}</Badge>}
        </div>
      </div>

      {area.danger && (
        <div className="mt-5 rounded-xl border border-plum/30 bg-plum-soft p-4 text-sm leading-relaxed text-ink" role="note">
          These links power the "Get Help Now" button and crisis actions on every page. They cannot be removed or
          broken — the server rejects unsafe values. Publishing a change here asks for extra confirmation.
        </div>
      )}

      <div className="mt-6 rounded-2xl border border-sage/15 bg-paper/40 p-5 sm:p-7">
        {isList ? (
          <ListPart
            name="items"
            label=""
            itemSchema={area.listSchema.itemSchema}
            items={draft}
            onChange={setDraft}
            addLabel={area.listSchema.addLabel}
            itemTitle={area.listSchema.itemTitle}
            idPrefix={`ed-${area.id}`}
          />
        ) : (
          <div className="space-y-8">
            {area.fieldSchema && (
              <FieldsPart schema={area.fieldSchema} value={draft} onChange={setDraft} idPrefix={`ed-${area.id}`} />
            )}
            {(area.lists || []).map((l) => (
              <ListPart
                key={l.name}
                name={l.name}
                label={l.label}
                itemSchema={l.itemSchema}
                items={draft[l.name]}
                onChange={(v) => setDraft({ ...draft, [l.name]: v })}
                addLabel={l.addLabel}
                itemTitle={l.itemTitle}
                idPrefix={`ed-${area.id}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button type="button" onClick={saveDraft} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-sage/30 px-5 py-2.5 text-sm font-semibold text-sage hover:bg-sage-soft disabled:opacity-50" data-testid="editor-save-draft-button">
          <Save className="h-4 w-4" aria-hidden="true" /> Save draft
        </button>
        <button type="button" onClick={publish} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-sage px-5 py-2.5 text-sm font-semibold text-cream hover:bg-sage-light disabled:opacity-50" data-testid="editor-publish-button">
          <Send className="h-4 w-4" aria-hidden="true" /> Publish
        </button>
        <button type="button" onClick={preview} className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-sage/30 px-5 py-2.5 text-sm font-semibold text-sage hover:bg-sage-soft" data-testid="editor-preview-button">
          <Eye className="h-4 w-4" aria-hidden="true" /> Preview site
        </button>
        {meta.draft != null && (
          <button type="button" onClick={discard} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-body hover:bg-paper" data-testid="editor-discard-button">
            <Ban className="h-4 w-4" aria-hidden="true" /> Discard draft
          </button>
        )}
        {meta.revisions > 0 && (
          <button type="button" onClick={restore} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-body hover:bg-paper" data-testid="editor-restore-button">
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Restore previous
          </button>
        )}
        {(meta.published != null || meta.draft != null) && (
          <button type="button" onClick={reset} disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold text-red-700 hover:bg-red-50" data-testid="editor-reset-button">
            Reset to defaults
          </button>
        )}
      </div>
      <div className="mt-4">
        <StatusMsg status={status} />
      </div>
      {meta.updatedAt && (
        <p className="mt-3 text-xs text-muted-foreground">
          Last changed {new Date(meta.updatedAt).toLocaleString()} {meta.updatedBy ? `by ${meta.updatedBy}` : ""}
        </p>
      )}
    </div>
  );
}

const QUICK_EDITS = [
  { id: "images", title: "Change the homepage picture", desc: "The big banner at the top of Home, the logo, and TJ's photos." },
  { id: "gallery", title: "Add or change gallery photos", desc: "The Photos page and the picture strip on About TJ. Upload straight from your phone or computer." },
  { id: "event", title: "Update walk details", desc: "Date, place, route map, flyer, merchandise, and contacts." },
  { id: "submissions", title: "Read form messages", desc: "Walk registrations, volunteer interest, sponsor and contact messages." },
];

function Overview({ summary, goArea }) {
  return (
    <div data-testid="admin-overview">
      <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">Welcome</h2>
      <p className="mt-1 max-w-2xl text-sm text-body">
        No code needed — everything on the website can be changed from these pages. Pick a
        quick edit below, or choose any area from the menu. Remember: <strong className="text-ink">Save draft</strong> keeps
        your work private, <strong className="text-ink">Publish</strong> makes it live.
      </p>
      <h3 className="mt-6 font-serif text-xl font-medium text-ink">Quick edits — the things you'll do most</h3>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {QUICK_EDITS.map((q) => (
          <button
            key={q.id}
            type="button"
            onClick={() => goArea(q.id)}
            className="rounded-xl border-2 border-gold/40 bg-gold-soft/30 p-4 text-left transition-colors hover:bg-gold-soft/60"
            data-testid={`quick-${q.id}`}
          >
            <p className="font-semibold text-ink">{q.title}</p>
            <p className="mt-0.5 text-sm leading-relaxed text-body">{q.desc}</p>
          </button>
        ))}
      </div>
      <h3 className="mt-8 font-serif text-xl font-medium text-ink">All content areas</h3>
      <p className="mt-1 max-w-2xl text-sm text-body">
        "Defaults" means the site is showing its original content until you publish your own.
      </p>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {AREAS.map((a) => {
          const s = summary?.[a.key];
          return (
            <button
              key={a.id}
              type="button"
              onClick={() => goArea(a.id)}
              className="rounded-xl border border-sage/15 bg-white p-5 text-left transition-colors hover:border-sage/40 hover:bg-paper/50"
              data-testid={`overview-card-${a.id}`}
            >
              <p className="font-semibold text-ink">{a.label}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {s?.hasPublished ? <Badge tone="good">Published</Badge> : <Badge>Defaults</Badge>}
                {s?.hasDraft && <Badge tone="warn">Draft</Badge>}
                {typeof s?.placeholders === "number" && s.placeholders > 0 && (
                  <Badge tone="warn">{s.placeholders} placeholder{s.placeholders === 1 ? "" : "s"}</Badge>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const TYPE_LABELS = { contact: "Contact", volunteer: "Volunteer", sponsor: "Sponsor / partner", registration: "Walk registration" };

function Submissions() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState(null);
  const load = () => api("/admin/submissions").then(setItems).catch((e) => setStatus({ kind: "error", msg: e.message }));
  useEffect(() => { load(); }, []);
  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await api(`/admin/submissions/${id}`, { method: "DELETE" });
      load();
    } catch (e) {
      setStatus({ kind: "error", msg: e.message });
    }
  };
  if (!items) return <p role="status" className="p-8 text-body">Loading submissions…</p>;
  return (
    <div data-testid="admin-submissions">
      <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">Form submissions</h2>
      <div className="mt-3 max-w-2xl rounded-xl border border-plum/30 bg-plum-soft p-4 text-sm leading-relaxed text-ink" role="note">
        Submissions are private and visible only here. This inbox is not monitored as a crisis service — if a
        message suggests someone may be in danger, encourage them toward 988 or 911 through the public pages.
      </div>
      <StatusMsg status={status} />
      {items.length === 0 ? (
        <p className="mt-6 text-sm text-body" data-testid="submissions-empty">No submissions yet.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {items.map((it) => (
            <article key={it.id} className="rounded-xl border border-sage/15 bg-white p-5" data-testid={`submission-${it.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-sage">
                  {TYPE_LABELS[it.type] || it.type} · {new Date(it.created_at).toLocaleString()}
                </p>
                <button type="button" onClick={() => remove(it.id)} className="min-h-[40px] rounded-md px-3 text-sm font-semibold text-red-700 hover:bg-red-50" data-testid={`submission-${it.id}-delete`}>
                  Delete
                </button>
              </div>
              <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {Object.entries(it.data).map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <dt className="font-semibold text-ink">{k}</dt>
                    <dd className="whitespace-pre-wrap text-body">{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function AuditLog() {
  const [items, setItems] = useState(null);
  useEffect(() => { api("/admin/audit").then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <p role="status" className="p-8 text-body">Loading audit log…</p>;
  return (
    <div data-testid="admin-audit">
      <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">Audit log</h2>
      <p className="mt-1 text-sm text-body">The most recent 100 important changes and sign-ins.</p>
      {items.length === 0 ? (
        <p className="mt-6 text-sm text-body">No activity yet.</p>
      ) : (
        <ul className="mt-6 space-y-2">
          {items.map((it, i) => (
            <li key={i} className="flex flex-wrap items-baseline gap-x-3 rounded-lg border border-sage/10 bg-white px-4 py-3 text-sm">
              <span className="text-muted-foreground">{new Date(it.ts).toLocaleString()}</span>
              <span className="font-semibold text-ink">{it.action}</span>
              {it.key && <span className="text-sage">{it.key}</span>}
              <span className="text-muted-foreground">{it.user}</span>
              {it.detail && <span className="text-muted-foreground">· {it.detail}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Account({ user, onSignOut }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next !== confirm) {
      setStatus({ kind: "error", msg: "New passwords do not match." });
      return;
    }
    setBusy(true);
    setStatus(null);
    try {
      await api("/auth/change-password", { method: "POST", body: { current, new: next } });
      setStatus({ kind: "ok", msg: "Password changed. Use the new password next time you sign in." });
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      setStatus({ kind: "error", msg: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md" data-testid="admin-account">
      <h2 className="font-serif text-2xl font-medium text-ink sm:text-3xl">Account</h2>
      <p className="mt-1 text-sm text-body">Signed in as <strong className="text-ink">{user.email}</strong>.</p>
      <form onSubmit={submit} className="mt-6 space-y-4" data-testid="change-password-form">
        {[
          { id: "pw-current", label: "Current password", value: current, set: setCurrent, auto: "current-password" },
          { id: "pw-new", label: "New password (at least 10 characters)", value: next, set: setNext, auto: "new-password" },
          { id: "pw-confirm", label: "Repeat new password", value: confirm, set: setConfirm, auto: "new-password" },
        ].map((f) => (
          <div key={f.id}>
            <label htmlFor={f.id} className="block text-sm font-semibold text-ink">{f.label}</label>
            <input
              id={f.id} type="password" required autoComplete={f.auto} value={f.value}
              onChange={(e) => f.set(e.target.value)}
              className="mt-1.5 block w-full min-h-[48px] rounded-lg border border-sage/25 px-4 py-3 text-base focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage-light/40"
              data-testid={`${f.id}-input`}
            />
          </div>
        ))}
        <button type="submit" disabled={busy} className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-sage px-6 py-2.5 text-sm font-semibold text-cream hover:bg-sage-light disabled:opacity-50" data-testid="change-password-submit">
          <KeyRound className="h-4 w-4" aria-hidden="true" /> {busy ? "Saving…" : "Change password"}
        </button>
      </form>
      <div className="mt-4"><StatusMsg status={status} /></div>
      <button type="button" onClick={onSignOut} className="mt-8 inline-flex min-h-[44px] items-center gap-2 rounded-full border border-sage/30 px-6 py-2.5 text-sm font-semibold text-sage hover:bg-sage-soft" data-testid="sign-out-button">
        <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
      </button>
    </div>
  );
}

const CUSTOM_AREAS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "submissions", label: "Submissions", icon: Inbox },
  { id: "audit", label: "Audit log", icon: History },
  { id: "account", label: "Account", icon: KeyRound },
];

export default function AdminApp() {
  const [user, setUser] = useState(undefined);
  const [area, setArea] = useState("overview");
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api("/auth/me").then(setUser).catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) api("/admin/content").then(setSummary).catch(() => {});
  }, [user, area]);

  const signOut = async () => {
    try { await api("/auth/logout", { method: "POST" }); } catch {}
    setToken(null);
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream">
        <p role="status" className="font-serif text-xl italic text-sage">Checking sign-in…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-cream px-4">
        <div className="w-full max-w-md rounded-2xl border border-sage/15 bg-white p-6 shadow-sm sm:p-8" data-testid="admin-login-page">
          <div className="flex items-center gap-2.5">
            <Logo className="h-9 w-auto" />
            <h1 className="font-serif text-2xl font-medium text-ink">Administrator sign-in</h1>
          </div>
          <p className="mt-2 text-sm text-body">
            This area is for the authorized site owner only. There is no public registration.
          </p>
          <div className="mt-6">
            <LoginForm autoFocus onSuccess={(u) => setUser(u)} />
          </div>
          <p className="mt-6 text-center text-sm">
            <Link to="/" className="font-semibold text-sage underline underline-offset-4" data-testid="admin-back-to-site">
              Back to the website
            </Link>
          </p>
        </div>
      </div>
    );
  }

  const active = AREAS.find((a) => a.id === area);
  const navItems = [
    ...CUSTOM_AREAS.slice(0, 1),
    ...AREAS.map((a) => ({ id: a.id, label: a.label, icon: AREA_ICONS[a.id] || Globe })),
    ...CUSTOM_AREAS.slice(1),
  ];

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-sage/10 bg-white">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
          <div className="flex items-center gap-2.5">
            <Logo className="h-9 w-auto" />
            <div>
              <p className="font-serif text-lg font-semibold text-ink">TJ's Light — Admin</p>
              <p className="text-xs text-muted-foreground">Signed in as {user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a href="/?preview=1" target="_blank" rel="noopener noreferrer" className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full border border-sage/30 px-4 py-2 text-sm font-semibold text-sage hover:bg-sage-soft" data-testid="admin-preview-site-link">
              <ExternalLink className="h-4 w-4" aria-hidden="true" /> Preview site
            </a>
            <button type="button" onClick={signOut} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full bg-sage px-4 py-2 text-sm font-semibold text-cream hover:bg-sage-light" data-testid="admin-sign-out-button">
              <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
          <nav aria-label="Admin areas" className="lg:col-span-3">
            <label htmlFor="admin-area-select" className="mb-2 block text-sm font-semibold text-ink lg:hidden">
              Choose an area
            </label>
            <select
              id="admin-area-select"
              value={area}
              onChange={(e) => setArea(e.target.value)}
              className="mb-4 block w-full min-h-[48px] rounded-lg border border-sage/25 bg-white px-3 py-2.5 text-sm lg:hidden"
              data-testid="admin-area-select"
            >
              {navItems.map((n) => (
                <option key={n.id} value={n.id}>{n.label}</option>
              ))}
            </select>
            <ul className="hidden space-y-1 lg:block">
              {navItems.map((n) => {
                const Icon = n.icon;
                const isActive = area === n.id;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => setArea(n.id)}
                      aria-current={isActive ? "page" : undefined}
                      className={`flex min-h-[44px] w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-colors ${
                        isActive ? "bg-sage text-cream" : "text-body hover:bg-sage-soft"
                      }`}
                      data-testid={`admin-nav-${n.id}`}
                    >
                      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> {n.label}
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <main className="lg:col-span-9">
            {area === "overview" && <Overview summary={summary} goArea={setArea} />}
            {area === "submissions" && <Submissions />}
            {area === "audit" && <AuditLog />}
            {area === "account" && <Account user={user} onSignOut={signOut} />}
            {active && <ContentEditor area={active} />}
          </main>
        </div>
      </div>
    </div>
  );
}
